import Head from 'next/head';
import React, { useState } from 'react';
import { Box, Container } from '@mui/material';
import BaseLayout from 'layouts/BaseLayout';
import Header from 'components/Templates/Header';
import List from 'components/Templates/List';
import AboutSection from 'components/Templates/AboutSection';
import FormatsService from 'services/FormatsService';
import TemplatesService from 'services/TemplatesService';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import useIsMobile from 'hooks/useIsMobile';

function getFormatListData(slug) {
  const data = {
    slides: {
      columns: 5,
      perPage: 15,
    },
    abstracts: {
      columns: 3,
      perPage: 9,
    },
    infographics: {
      columns: 6,
      perPage: 18,
    },
    posters: {
      columns: 5,
      perPage: 15,
    },
  };

  if (data[slug]) {
    return data[slug];
  }

  throw new Error(`Unable to get format list data for unknown format [${slug}].`);
}

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params: { slug } }) {
  try {
    const { data: format } = await FormatsService.fetchOneBySlug(slug);
    const { data: templates } = await TemplatesService.fetchAll({
      formatId: format.id,
      page: 1,
      perPage: getFormatListData(format.slug).perPage,
      neutral: true,
    });

    return {
      props: {
        format,
        templates,
      },
      revalidate: 604800, // 1 week
    };
  } catch (e) {
    console.error(e);

    return {
      props: {
        format: null,
        templates: {},
      },
      revalidate: 604800, // 1 week
    };
  }
}

export default function TemplatesFormatPage({ format, templates: initialTemplates }) {
  const [templates, setTemplates] = useState(initialTemplates);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();

  async function fetchTemplates(page) {
    try {
      const { data } = await TemplatesService.fetchAll({
        formatId: format.id,
        page,
        perPage: templates.perPage,
        neutral: true,
      });

      setTemplates(data);
    } catch (e) {
      console.error(e);
    }
  }

  function handlePaginationChange(page) {
    if (page !== currentPage) {
      setCurrentPage(page);

      fetchTemplates(page);
    }
  }

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>Neutral Infographic templates - Mind the Graph</title>
      </Head>

      <Header />

      <Box py={6} bgcolor="common.white">
        <Container>
          <List
            breadcrumbItems={[
              {
                label: 'Neutral Templates',
                path: '/infographic-templates/category/neutral',
              },
              {
                label: format.name,
              },
            ]}
            formatName={`Neutral ${format.name}`}
            templates={templates}
            columns={getFormatListData(format.slug).columns}
            currentPage={currentPage}
            onPaginationChange={handlePaginationChange}
          />
        </Container>
      </Box>

      <AboutSection />
    </BaseLayout>
  );
}
